// Client-side mirror of `EmailGuard` (app/lib/email_guard.rb). Three checks,
// short-circuited in order: format → typo → disposable. Server enforces the
// same rules; this just gives fast feedback.

import { isDisposableEmail } from './email-blocklist';
import { suggestEmail } from './email-typo';

export type EmailValidationCode = 'format' | 'typo' | 'disposable';

export interface EmailValidationResult {
  ok: boolean;
  code?: EmailValidationCode;
  suggestion?: string;
}

// Stricter than the typical `.email()` regex: rejects leading/trailing dots
// in the local part, double dots anywhere, single-label domains, empty TLDs,
// and TLDs outside 2..24 ASCII letters.
export const STRICT_EMAIL_REGEXP =
  /^(?!.*\.\.)[A-Z0-9](?:[A-Z0-9._%+\-]*[A-Z0-9])?@(?:[A-Z0-9](?:[A-Z0-9\-]*[A-Z0-9])?\.)+[A-Z]{2,24}$/i;

export function isMalformed(email: string): boolean {
  return !STRICT_EMAIL_REGEXP.test(email);
}

export function validateEmail(input: string): EmailValidationResult {
  const email = input.trim();

  if (isMalformed(email)) {
    return { ok: false, code: 'format' };
  }

  const suggestion = suggestEmail(email);
  if (suggestion) {
    return { ok: false, code: 'typo', suggestion };
  }

  if (isDisposableEmail(email)) {
    return { ok: false, code: 'disposable' };
  }

  return { ok: true };
}
